import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { toPng, getFontEmbedCSS } from "html-to-image";
import { DownloadSimple, ClockCounterClockwise } from "@phosphor-icons/react";
import { useApi, fmtDate } from "@/lib/api";
import { RANGES } from "@/lib/chartRange";

const pill = "flex items-center gap-1 rounded-full border h-8 px-3 text-[11px] transition-colors disabled:opacity-40 disabled:cursor-not-allowed";

export const useLastWeek = () => {
  const api = useApi();
  const [on, setOn] = useState(false);
  const { data } = useQuery({ queryKey: ["forecast-last-week"], queryFn: () => api.get("/forecast/last-week") });
  return { on: on && !!data?.available, setOn, snapshot: data };
};

export const CompareToggle = ({ on, setOn, snapshot }) => {
  const available = !!snapshot?.available;
  const title = available ? `Compare with the forecast saved at the ${fmtDate(snapshot.taken_at)} digest` : "Available after next Monday's digest";
  return (
    <button data-testid="chart-compare-btn" onClick={() => setOn(!on)} disabled={!available} title={title} aria-pressed={on}
      className={`${pill} ${on ? "border-[#A3A3A3] bg-white/10 text-white" : "border-[#2A2A2A] text-[#A3A3A3] hover:border-[#A3A3A3] hover:text-white"}`}>
      <ClockCounterClockwise size={12} /> {available ? "vs last Monday" : "Last Monday: next week"}
    </button>
  );
};

const brand = async (node, range) => {
  const img = new Image();
  let fontEmbedCSS;
  try { fontEmbedCSS = await getFontEmbedCSS(node); } catch { fontEmbedCSS = undefined; }
  img.src = await toPng(node, { pixelRatio: 2, backgroundColor: "#121212", fontEmbedCSS, filter: (n) => !n.dataset?.exportIgnore });
  await img.decode();
  const pad = 48, head = 88;
  const canvas = document.createElement("canvas");
  canvas.width = img.width + pad * 2;
  canvas.height = img.height + head + pad;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#050505";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, pad, head);
  const mark = new Image();
  mark.src = "/calibrunway-mark.png";
  let x = pad;
  try { await mark.decode(); ctx.drawImage(mark, pad, 22, 44, 44); x = pad + 56; } catch { /* text-only header */ }
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "700 30px 'Space Grotesk', sans-serif";
  ctx.fillText("Calib", x, 58);
  const w = ctx.measureText("Calib").width;
  ctx.fillStyle = "#00FF9D";
  ctx.fillText("Runway", x + w, 58);
  ctx.fillStyle = "#A3A3A3";
  ctx.font = "400 22px 'JetBrains Mono', monospace";
  const meta = `Runway forecast · ${RANGES.find(([k]) => k === range)[1]} · ${new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
  ctx.textAlign = "right";
  ctx.fillText(meta, canvas.width - pad, 56);
  return canvas.toDataURL("image/png");
};

export const ExportChartButton = ({ target, range }) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!target.current) return;
    setBusy(true);
    try {
      const a = document.createElement("a");
      a.href = await brand(target.current, range);
      a.download = `calibrunway-runway-${range}-${new Date().toISOString().slice(0, 10)}.png`;
      a.click();
      toast.success("Chart image downloaded");
    } catch { toast.error("Couldn't export the chart"); }
    setBusy(false);
  };
  return (
    <button data-testid="chart-export-btn" onClick={run} disabled={busy}
      className={`${pill} border-[#2A2A2A] text-[#A3A3A3] hover:border-[#00FF9D] hover:text-[#00FF9D]`}>
      <DownloadSimple size={12} /> {busy ? "Exporting…" : "Export image"}
    </button>
  );
};
